import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trophy, X } from "lucide-react";
import {
  fetchAdminAchievements,
  createAdminAchievement,
  patchAdminAchievement,
  deleteAdminAchievement,
  type AdminAchievement,
  type AdminAchievementTier,
  type AchievementRuleType,
} from "../../api/admin-content";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 32,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const label: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 6,
};

const TIERS: AdminAchievementTier[] = ["bronze", "silver", "gold", "platinum", "mythic"];

// Mirrors packages/db/src/achievement-rules.ts evaluateRule exactly — the
// rule engine is fixed, this only exposes its existing shapes as a form.
const RULE_TYPES: { value: AchievementRuleType; label: string }[] = [
  { value: "peak_count", label: "Peak count" },
  { value: "peak_elevation", label: "Peak elevation" },
  { value: "multi_peak_day", label: "Multi-peak day" },
  { value: "activity_distance", label: "Activity distance" },
  { value: "early_start", label: "Early start" },
  { value: "collection_complete", label: "Collection complete" },
  { value: "trail_complete", label: "Trail complete" },
  { value: "landmark_count", label: "Landmark count" },
  { value: "distinct_countries", label: "Distinct countries" },
  { value: "region_coverage", label: "Region coverage" },
  { value: "total_distance", label: "Lifetime distance" },
  { value: "total_elevation_gain", label: "Lifetime elevation gain" },
  { value: "total_activities", label: "Lifetime activities" },
  { value: "total_days_out", label: "Lifetime days out" },
  { value: "peak_summited", label: "Specific peak(s) summited" },
  { value: "peak_count_country", label: "Peak count in country" },
  { value: "landmark_category_count", label: "Landmark category count" },
  { value: "collection_pct", label: "Collection percentage" },
  { value: "collections_completed_count", label: "Collections completed" },
  { value: "trails_completed_count", label: "Trails completed" },
  { value: "trails_completed_in_set", label: "Trails completed from a set" },
  { value: "collections_complete_all", label: "Every item of several collections" },
  { value: "trail_every_route", label: "Trail completed by every route" },
  { value: "trail_sections_count", label: "Trail sections completed" },
  { value: "regions_fully_explored_count", label: "Regions fully explored" },
  { value: "activity_elevation_gain", label: "Single-activity elevation gain" },
  { value: "activity_duration", label: "Activity duration (verified times)" },
  { value: "finish_after_hour", label: "Finish after hour (verified times)" },
  { value: "spans_midnight", label: "Spans midnight (verified times)" },
  { value: "activity_on_date", label: "Activity on date (verified times)" },
  { value: "months_in_year", label: "Months in one year (verified times)" },
  { value: "distinct_seasons", label: "Distinct seasons (verified times)" },
  { value: "same_day_count", label: "Activities in one day (verified times)" },
  { value: "weekly_streak", label: "Weekly streak (verified times)" },
  { value: "summit_month", label: "Summit in month(s) (verified times)" },
  { value: "collection_in_one_activity", label: "Collection in one activity (verified times)" },
  { value: "trail_complete_in_one_activity", label: "Whole trail in one activity (verified times)" },
];

type RuleFieldType = "number" | "string" | "numbers" | "strings" | "boolean";

const RULE_PARAM_FIELDS: Record<
  AchievementRuleType,
  { key: string; label: string; type: RuleFieldType; optional?: boolean }[]
> = {
  peak_count: [{ key: "count", label: "Count", type: "number" }],
  peak_elevation: [{ key: "metres", label: "Metres", type: "number" }],
  multi_peak_day: [
    { key: "count", label: "Peaks in one day", type: "number" },
    { key: "minElevationM", label: "Min elevation (m, optional)", type: "number", optional: true },
  ],
  activity_distance: [{ key: "distanceM", label: "Distance (m)", type: "number" }],
  early_start: [{ key: "beforeHour", label: "Before hour (0–23, optional)", type: "number", optional: true }],
  collection_complete: [{ key: "collectionSlug", label: "Collection slug", type: "string" }],
  trail_complete: [{ key: "trailSlug", label: "Trail slug", type: "string" }],
  landmark_count: [{ key: "count", label: "Count", type: "number" }],
  distinct_countries: [{ key: "count", label: "Count", type: "number" }],
  region_coverage: [
    { key: "regionSlug", label: "Region slug", type: "string" },
    { key: "pct", label: "Coverage %", type: "number" },
  ],
  total_distance: [{ key: "distanceM", label: "Distance (m)", type: "number" }],
  total_elevation_gain: [{ key: "metres", label: "Metres", type: "number" }],
  total_activities: [{ key: "count", label: "Count", type: "number" }],
  total_days_out: [{ key: "count", label: "Count", type: "number" }],
  peak_summited: [
    { key: "peakSlug", label: "Peak slug (single)", type: "string", optional: true },
    { key: "peakSlugs", label: "Peak slugs (comma-separated)", type: "strings", optional: true },
    { key: "mode", label: "Mode (all | any)", type: "string", optional: true },
  ],
  peak_count_country: [
    { key: "country", label: "Country", type: "string" },
    { key: "count", label: "Count", type: "number" },
  ],
  landmark_category_count: [
    { key: "category", label: "Landmark category", type: "string" },
    { key: "count", label: "Count", type: "number" },
  ],
  collection_pct: [
    { key: "collectionSlug", label: "Collection slug", type: "string" },
    { key: "pct", label: "Completion %", type: "number" },
  ],
  collections_completed_count: [{ key: "count", label: "Count", type: "number" }],
  trails_completed_count: [{ key: "count", label: "Count", type: "number" }],
  collections_complete_all: [
    { key: "collectionSlugs", label: "Collection slugs (comma-separated)", type: "strings" },
  ],
  trails_completed_in_set: [
    { key: "trailSlugs", label: "Trail slugs (comma-separated)", type: "strings" },
    { key: "count", label: "Count", type: "number" },
  ],
  trail_every_route: [{ key: "trailSlug", label: "Trail slug", type: "string" }],
  trail_sections_count: [
    { key: "count", label: "Count", type: "number" },
    // ATL-95: optional, scopes the count to one trail (England Coast Path).
    { key: "trailSlug", label: "Trail slug (optional, one trail only)", type: "string", optional: true },
  ],
  regions_fully_explored_count: [{ key: "count", label: "Count", type: "number" }],
  activity_elevation_gain: [
    { key: "metres", label: "Metres", type: "number" },
    { key: "requireTrusted", label: "Verified times only", type: "boolean", optional: true },
  ],
  activity_duration: [{ key: "hours", label: "Hours", type: "number" }],
  finish_after_hour: [{ key: "hour", label: "Hour (0–23)", type: "number" }],
  spans_midnight: [],
  activity_on_date: [
    { key: "month", label: "Month (1–12)", type: "number" },
    { key: "day", label: "Day (1–31)", type: "number" },
  ],
  months_in_year: [{ key: "count", label: "Months", type: "number" }],
  distinct_seasons: [{ key: "count", label: "Seasons", type: "number" }],
  same_day_count: [{ key: "count", label: "Activities in one day", type: "number" }],
  weekly_streak: [{ key: "weeks", label: "Weeks", type: "number" }],
  summit_month: [{ key: "months", label: "Months (comma-separated, 1–12)", type: "numbers" }],
  collection_in_one_activity: [{ key: "collectionSlug", label: "Collection slug", type: "string" }],
  trail_complete_in_one_activity: [{ key: "trailSlug", label: "Trail slug", type: "string" }],
};

function RuleParamFields({
  ruleType,
  values,
  onChange,
}: {
  ruleType: AchievementRuleType;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  const fields = RULE_PARAM_FIELDS[ruleType];
  if (fields.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>This rule needs no parameters.</p>;
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: fields.length > 1 ? "1fr 1fr" : "1fr", gap: 12 }}>
      {fields.map((f) => (
        <div key={f.key}>
          <label style={label}>{f.label}</label>
          {f.type === "boolean" ? (
            <input
              type="checkbox"
              checked={values[f.key] === "true"}
              onChange={(e) => onChange(f.key, e.target.checked ? "true" : "")}
            />
          ) : (
            <input
              type={f.type === "number" ? "number" : "text"}
              value={values[f.key] ?? ""}
              onChange={(e) => onChange(f.key, e.target.value)}
              style={inputStyle}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function buildRuleParams(
  ruleType: AchievementRuleType,
  values: Record<string, string>,
): Record<string, unknown> {
  const fields = RULE_PARAM_FIELDS[ruleType];
  const params: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = (values[f.key] ?? "").trim();
    if (f.optional && !raw) continue;
    switch (f.type) {
      case "number":
        params[f.key] = parseFloat(raw);
        break;
      case "boolean":
        params[f.key] = raw === "true";
        break;
      case "numbers":
        params[f.key] = raw.split(",").map((v) => parseFloat(v.trim())).filter((v) => Number.isFinite(v));
        break;
      case "strings":
        params[f.key] = raw.split(",").map((v) => v.trim()).filter(Boolean);
        break;
      default:
        params[f.key] = raw;
    }
  }
  return params;
}

function ruleParamsToValues(ruleType: AchievementRuleType, params: Record<string, unknown>): Record<string, string> {
  const fields = RULE_PARAM_FIELDS[ruleType];
  const values: Record<string, string> = {};
  for (const f of fields) {
    const v = params[f.key];
    if (v === undefined || v === null) {
      values[f.key] = "";
    } else if (Array.isArray(v)) {
      values[f.key] = v.join(", ");
    } else {
      values[f.key] = String(v);
    }
  }
  return values;
}

// ─── Achievement drawer (create + edit) ────────────────────────────────────────

function AchievementDrawer({
  achievement,
  onClose,
}: {
  achievement: AdminAchievement | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isCreate = achievement === null;

  const [name, setName] = React.useState(achievement?.name ?? "");
  const [slug, setSlug] = React.useState(achievement?.slug ?? "");
  const [description, setDescription] = React.useState(achievement?.description ?? "");
  const [tier, setTier] = React.useState<AdminAchievementTier>(achievement?.tier ?? "bronze");
  const [points, setPoints] = React.useState(String(achievement?.points ?? 10));
  const [iconName, setIconName] = React.useState(achievement?.iconName ?? "");
  const [category, setCategory] = React.useState(achievement?.category ?? "");
  const [ruleType, setRuleType] = React.useState<AchievementRuleType>(achievement?.ruleType ?? "peak_count");
  const [ruleValues, setRuleValues] = React.useState<Record<string, string>>(
    achievement ? ruleParamsToValues(achievement.ruleType, achievement.ruleParams) : {},
  );

  function handleRuleTypeChange(next: AchievementRuleType) {
    setRuleType(next);
    setRuleValues({});
  }

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["admin/content/achievements"] });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminAchievement({
        slug: slug.trim() || undefined,
        name: name.trim(),
        description: description.trim(),
        tier,
        points: parseInt(points, 10) || 0,
        iconName: iconName.trim() || null,
        category: category.trim() || null,
        ruleType,
        ruleParams: buildRuleParams(ruleType, ruleValues),
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!achievement) throw new Error("No achievement to update");
      return patchAdminAchievement(achievement.id, {
        name: name.trim(),
        description: description.trim(),
        tier,
        points: parseInt(points, 10) || 0,
        iconName: iconName.trim() || null,
        category: category.trim() || null,
        ruleType,
        ruleParams: buildRuleParams(ruleType, ruleValues),
      });
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!achievement) throw new Error("No achievement to delete");
      return deleteAdminAchievement(achievement.id);
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const busy = createMutation.isPending || patchMutation.isPending;
  const err = createMutation.error ?? patchMutation.error ?? deleteMutation.error;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={onClose} />
      <div
        style={{
          position: "relative",
          width: 460,
          maxWidth: "92vw",
          height: "100%",
          background: "var(--surface-card)",
          borderLeft: "1px solid var(--border-strong)",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 22px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
              {isCreate ? "New achievement" : "Achievement"}
            </p>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>
              {achievement?.name ?? "Untitled"}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "16px 22px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Munro Bagger" />
            </div>
            <div>
              <label style={label}>Tier</label>
              <select value={tier} onChange={(e) => setTier(e.target.value as AdminAchievementTier)} style={{ ...inputStyle, cursor: "pointer" }}>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {isCreate && (
            <div>
              <label style={label}>Slug (optional — derived from name)</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} placeholder="munro-bagger" />
            </div>
          )}

          <div>
            <label style={label}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              style={{ ...inputStyle, height: "auto", padding: 8, resize: "vertical" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Points</label>
              <input type="number" value={points} onChange={(e) => setPoints(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={label}>Icon name</label>
              <input value={iconName} onChange={(e) => setIconName(e.target.value)} style={inputStyle} placeholder="mountain" />
            </div>
            <div>
              <label style={label}>Category</label>
              <input value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />

          <div>
            <label style={label}>Rule type</label>
            <select
              value={ruleType}
              onChange={(e) => handleRuleTypeChange(e.target.value as AchievementRuleType)}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              {RULE_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <RuleParamFields
            ruleType={ruleType}
            values={ruleValues}
            onChange={(key, value) => setRuleValues((prev) => ({ ...prev, [key]: value }))}
          />

          {err && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
              {errorMessage(err, "Something went wrong")}
            </p>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              variant="secondary"
              disabled={busy || !name.trim() || !description.trim()}
              onClick={() => (isCreate ? createMutation.mutate() : patchMutation.mutate())}
            >
              {busy ? "Saving…" : isCreate ? "Create achievement" : "Save changes"}
            </Button>
          </div>

          {achievement && (
            <div
              style={{
                padding: "12px 14px",
                border: "1px solid rgba(224, 80, 63, 0.35)",
                borderRadius: "var(--radius-md)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>Delete achievement</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  {achievement.unlockedCount > 0
                    ? `${achievement.unlockedCount} user(s) have unlocked this — cannot be deleted.`
                    : "Not yet unlocked by anyone — safe to delete."}
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                disabled={deleteMutation.isPending || achievement.unlockedCount > 0}
                onClick={() => deleteMutation.mutate()}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AdminAchievementsPage ──────────────────────────────────────────────────────

export default function AdminAchievementsPage() {
  const { user } = useAuth();
  const [drawerAchievement, setDrawerAchievement] = React.useState<AdminAchievement | null | "new">(null);

  const achievementsQuery = useQuery({ queryKey: ["admin/content/achievements"], queryFn: fetchAdminAchievements });

  if (!user?.isAdmin) return <AccessDenied />;

  const rows = achievementsQuery.data?.achievements ?? [];
  const gridColumns = "minmax(0,1.4fr) 80px minmax(0,1.2fr) 60px 70px";
  const headerCell: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell
      title="Achievements"
      action={
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDrawerAchievement("new")}>
          New achievement
        </Button>
      }
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 10, padding: "10px 0 8px", borderBottom: "1px solid var(--border-subtle)", minWidth: 620 }}>
          <span style={headerCell}>Name</span>
          <span style={headerCell}>Tier</span>
          <span style={headerCell}>Rule</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Points</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Unlocked</span>
        </div>

        {achievementsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} /><Skel height={30} /><Skel height={30} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<Trophy size={20} />} title="No achievements yet" hint="Create the first one above." style={{ margin: "12px 0" }} />
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              onClick={() => setDrawerAchievement(row)}
              style={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap: 10,
                alignItems: "center",
                padding: "9px 0",
                borderBottom: "1px solid var(--border-subtle)",
                cursor: "pointer",
                minWidth: 620,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--surface-hover)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.name}
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{row.slug}</div>
              </div>
              <span><Badge variant={row.tier}>{row.tier}</Badge></span>
              <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.ruleType}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", textAlign: "right" }}>{row.points}</span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>{row.unlockedCount}</span>
            </div>
          ))
        )}
      </div>

      {drawerAchievement !== null && (
        <AchievementDrawer
          achievement={drawerAchievement === "new" ? null : drawerAchievement}
          onClose={() => setDrawerAchievement(null)}
        />
      )}
    </AdminShell>
  );
}
